import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { Link } from 'react-router';
import { __ } from '@bart.sk-eshop/react-i18n';
import {
  URL_REGISTRATION_RETAIL,
  URL_REGISTRATION_COMPANY,
  URL_REGISTRATION_SCHOOL,
} from '../../constants';
import { Content, Title } from '../_layout/Page/ContentContainer';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import { Container } from '../../theme/libraries/grid';
import { ButtonStyled } from '../_helpers/form/Button';
import { connectSsr } from '@bart.sk-eshop/ssr-service';
import { connect } from 'react-redux';
import { loadGlobalMetaTags } from '../../utilities';
import MetaTags from '../MetaTags/MetaTags';

const metaUid = 'REG_HUB';

const Wrapper = styled(Content)`
  margin-bottom: ${rem(20)};
  @media (min-width: 768px) {
    margin-bottom: ${rem(40)};
  }
`;

const RegistrationContainer = styled(Container)`
  padding-right: 0;
  padding-left: 0;
  @media (min-width: 768px) {
    padding-right: ${rem(15)};
    padding-left: ${rem(15)};
  }
`;

const Description = styled.p`
  font-size: ${rem(16)};
  color: #211e1f;
  margin: ${rem(-5)} 0 ${rem(10)} 0;
  text-align: center;

  @media (min-width: 768px) {
    font-size: ${rem(24)};
    margin: ${rem(20)} 0 ${rem(20)};
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  max-width: ${rem(1000)};
  margin: 0 auto;
  margin-bottom: ${rem(20)};
  margin-top: ${rem(0)};

  @media (min-width: 768px) {
    flex-direction: row;
    gap: ${rem(30)};
    margin-bottom: ${rem(60)};
    margin-top: ${rem(40)};
  }
`;

const OptionCard = styled.div`
  flex: 1;
  background: #f9f9f9;
  border-radius: ${rem(8)};
  padding: ${rem(15)};
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${rem(20)};
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    padding: ${rem(15)};
    gap: ${rem(20)};
  }

  /* &:hover {
    background: #f4f4f4;
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  } */
`;

const IconWrapper = styled.div`
  width: ${rem(35)};
  height: ${rem(35)};
  flex-shrink: 0;
  margin-top: ${rem(8)};

  @media (min-width: 768px) {
    width: ${rem(35)};
    height: ${rem(35)};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const OptionTitle = styled.h3`
  font-size: ${rem(16)};
  font-weight: bold;
  color: #211e1f;
  margin: 0 0 ${rem(0)};

  @media (min-width: 768px) {
    font-size: ${rem(18)};
    margin: 0 0 ${rem(0)};
  }
`;

const OptionDescription = styled.p`
  font-size: ${rem(15)};
  color: #575757;
  margin: 0 0 ${rem(10)};
  line-height: 1.4;

  @media (min-width: 768px) {
    font-size: ${rem(15)};
    margin: 0 0 ${rem(10)};
  }
`;

const RegisterButton = styled(ButtonStyled).attrs({
  as: Link,
})`
  max-width: fit-content;
  margin-top: auto;
`;

interface RegistrationHubProps {
  globalMetaTags: any;
}

const RegistrationHub = (props: RegistrationHubProps) => {
  const { globalMetaTags } = props;

  return (
    <RegistrationContainer className="base">
      <Wrapper>
        {globalMetaTags[metaUid] ? (
          <MetaTags tags={globalMetaTags[metaUid]} />
        ) : (
          <MetaTags tags={{ title: 'Daffer.sk - Registrácia' }} />
        )}
        <Breadcrumbs
          items={[
            { name: __('Môj účet'), url: '/prihlasenie' },
            { name: __('Registrácia'), url: '/registracia' },
          ]}
        />
        <Title>{__('Registrácia')}</Title>
        <Description>
          {__('Vyberte si, ako budete nakupovať - podľa toho nastavíme účet.')}
        </Description>

        <OptionsContainer>
          <OptionCard>
            <IconWrapper>
              <img
                src="/images/icons/registration/ico-osoba.svg"
                alt={__('Súkromná osoba')}
              />
            </IconWrapper>
            <ContentWrapper>
              <OptionTitle>{__('Súkromná osoba')}</OptionTitle>
              <OptionDescription>
                {__('Nakupujem pre seba a rodinu.')}
              </OptionDescription>
              <RegisterButton to={URL_REGISTRATION_RETAIL} className="primary">
                {__('Registrovať osobu')}
              </RegisterButton>
            </ContentWrapper>
          </OptionCard>

          <OptionCard>
            <IconWrapper>
              <img
                src="/images/icons/registration/ico-firma.svg"
                alt={__('Firma / organizácia')}
              />
            </IconWrapper>
            <ContentWrapper>
              <OptionTitle>{__('Firma / organizácia')}</OptionTitle>
              <OptionDescription>
                {__('Mám IČO, potrebujem faktúru.')}
              </OptionDescription>
              <RegisterButton to={URL_REGISTRATION_COMPANY} className="primary">
                {__('Registrovať firmu')}
              </RegisterButton>
            </ContentWrapper>
          </OptionCard>

          <OptionCard>
            <IconWrapper>
              <img
                src="/images/icons/registration/ico-skola.svg"
                alt={__('Školské zariadenie')}
              />
            </IconWrapper>
            <ContentWrapper>
              <OptionTitle>{__('Školské zariadenie')}</OptionTitle>
              <OptionDescription>
                {__('Potrebujem účet pre školu.')}
              </OptionDescription>
              <RegisterButton to={URL_REGISTRATION_SCHOOL} className="primary">
                {__('Registrovať školu')}
              </RegisterButton>
            </ContentWrapper>
          </OptionCard>
        </OptionsContainer>
      </Wrapper>
    </RegistrationContainer>
  );
};

RegistrationHub.getInitialProps = async (parentProps) => {
  const { dispatch } = parentProps;
  const promises: any[] = [];

  promises.push(dispatch(loadGlobalMetaTags(metaUid)));

  await Promise.all(promises);
};

const mapStateToProps = (state) => {
  return {
    globalMetaTags: state.general.globalMetaTags,
  };
};

export default connect(mapStateToProps)(
  connectSsr({ displayName: 'RegistrationHub' })(RegistrationHub),
);
